import { useState, useRef, useEffect, cloneElement } from "react";
import PropTypes from "prop-types";
import { DownOutlined } from "@/config/antd";
import BaseInput from "@/components/common/inputs/Base_Input";
import {
  getInputBorderClass,
  getInputBackgroundClass,
  getIconColorClass,
  getFocusClasses,
  getTransitionClasses,
  getBasePaddingClasses,
  isInputActive,
} from "@/helper/input_styles";

const SelectInput = ({
  label,
  placeholder,
  icon,
  value,
  onChange,
  className = "",
  options = [],
}) => {
  const [isFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Use shared styling utilities
  const isActive = isInputActive(value, isFocused);
  const iconColor = getIconColorClass(isActive);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <BaseInput label={label} ref={dropdownRef} data-testid="select-container">
      <div className="absolute inset-y-0 left-3 flex items-center">
        {icon && (
          <span
            className={`pr-3 border-r border-gray-300 ${iconColor}`}
            data-testid="select-icon">
            {cloneElement(icon, { className: iconColor })}
          </span>
        )}
      </div>
      <div
        className={`w-full ${getBasePaddingClasses(!!icon)}
          ${getInputBackgroundClass(isActive)}
          rounded-md leading-5
          ${isActive ? "text-gray-900 preserve-text-primary" : "text-gray-400"}
          ${getInputBorderClass(isActive)}
          ${getFocusClasses()}
          ${getTransitionClasses()} ${className} cursor-pointer text-left`}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="select-input">
        <span data-testid="selected-value">
          {value
            ? options.find((option) => option.value === value)?.label
            : placeholder}
        </span>
      </div>
      <DownOutlined
        className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${iconColor} pointer-events-none`}
        data-testid="down-icon"
      />
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
          data-testid="options-dropdown">
          {options.map((option) => (
            <div
              key={option.value}
              className="px-4 py-2 cursor-pointer hover:bg-primary hover:text-white transition-all duration-500 hover:font-bold text-left"
              onClick={() => {
                onChange({ target: { value: option.value } });
                setIsOpen(false);
              }}
              data-testid={`option-${option.value}`}>
              {option.label}
            </div>
          ))}
        </div>
      )}
    </BaseInput>
  );
};

SelectInput.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  id: PropTypes.string,
  icon: PropTypes.element,
  value: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ),
};

export default SelectInput;
